import React, { useEffect, useState, useCallback } from "react";
import { groupService } from "../services/groupService";
import { getApiUrl } from "../config/api";
import { fetchMaterialCatalog, getCatalogForCategory } from "../utils/MaterialCatalog";
import { formatDate } from "../utils/dateUtils";
import HistoryDetailsModal from "./HistoryDetailsModal";

function capitalize(str) {
  if (!str) return "";
  return str.replace(/-/g, ' ').charAt(0).toUpperCase() + str.replace(/-/g, ' ').slice(1);
}

// Convert kebab-case to Title Case for catalog lookup
function toTitleCase(str) {
  if (!str) return "";
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function Groups({ onOpenChat, clientId, onOpenRFQ }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedMaterials, setExpandedMaterials] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);

  useEffect(() => {
    loadCatalog();
  }, []);

  const loadGroups = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const data = await groupService.getAllGroups(clientId);
      setGroups(data);
      setError(null);
    } catch (err) {
      console.error('[Groups] Failed to load groups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (!clientId) return;

    let es;
    let retryTimeout;

    const handleHistoryMessage = (event) => {
      try {
        const raw = JSON.parse(event.data);
        console.log('[Groups] Received SSE update:', raw);
        
        if (!raw.newHistory || !Array.isArray(raw.newHistory) || raw.newHistory.length === 0) {
          return;
        }

        // Update RFQs in place instead of reloading all groups
        raw.newHistory.forEach(historyItem => {
          const rfq = historyItem.rfq;
          if (!rfq || !rfq.rfqNumber) return;

          console.log('[Groups] Updating RFQ:', rfq.rfqNumber);
          
          setGroups(prevGroups => {
            return prevGroups.map(group => {
              // Check if this group contains the RFQ
              const rfqIndex = group.rfqs.findIndex(r => r.rfqNumber === rfq.rfqNumber);
              if (rfqIndex === -1) return group;

              // Update the RFQ data
              const updatedRfqs = [...group.rfqs];
              updatedRfqs[rfqIndex] = {
                ...updatedRfqs[rfqIndex],
                ...rfq,
                // Preserve quote data if present in the update
                quote: historyItem.quote || rfq.quote || updatedRfqs[rfqIndex].quote,
                status: rfq.status || historyItem.status || updatedRfqs[rfqIndex].status,
                state: rfq.state || historyItem.state || updatedRfqs[rfqIndex].state,
              };

              return {
                ...group,
                rfqs: updatedRfqs
              };
            });
          });
        });
      } catch (err) {
        console.warn('[Groups] SSE message parse error:', err);
      }
    };

    const connect = () => {
      const streamUrl = getApiUrl(`/api/history/stream?clientId=${encodeURIComponent(clientId)}`);
      console.log('[Groups] Connecting to SSE stream at:', streamUrl);
      
      es = new EventSource(streamUrl);
      
      es.onopen = () => {
        console.log('[Groups] SSE connection opened');
      };
      
      es.onmessage = handleHistoryMessage;
      es.addEventListener("history", handleHistoryMessage);
      
      es.onerror = (error) => {
        console.error('[Groups] SSE connection error:', error);
        try { es.close(); } catch {}
        retryTimeout = setTimeout(() => {
          console.log('[Groups] Retrying SSE connection...');
          connect();
        }, 3000);
      };
    };

    connect();

    return () => {
      if (es) {
        try { es.close(); } catch {}
      }
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [clientId]);

  const loadCatalog = async () => {
    try {
      const catalogData = await fetchMaterialCatalog();
      setCatalog(catalogData);
    } catch (err) {
      console.error('[Groups] Failed to load catalog:', err);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const toggleMaterial = (groupId, material) => {
    const key = `${groupId}-${material}`;
    setExpandedMaterials(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleSubcategory = (groupId, material, subcategory) => {
    const key = `${groupId}-${material}-${subcategory}`;
    setExpandedSubcategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (!clientId) {
      alert('User not authenticated');
      return;
    }

    try {
      setCreatingGroup(true);
      await groupService.createGroup(clientId, newGroupName.trim());
      await loadGroups();
      setNewGroupName("");
      setShowCreateGroup(false);
    } catch (err) {
      console.error('[Groups] Failed to create group:', err);
      alert(`Failed to create group: ${err.message}`);
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete the group "${groupName}"? This will not delete the RFQs.`)) {
      return;
    }

    if (!clientId) {
      alert('User not authenticated');
      return;
    }

    try {
      setDeletingGroup(groupId);
      await groupService.deleteGroup(clientId, groupId);
      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to delete group:', err);
      alert(`Failed to delete group: ${err.message}`);
    } finally {
      setDeletingGroup(null);
    }
  };

  const handleRemoveRFQ = async (groupId, rfqNumber) => {
    if (!window.confirm(`Remove RFQ #${rfqNumber} from this group?`)) {
      return;
    }

    if (!clientId) {
      alert('User not authenticated');
      return;
    }

    try {
      await groupService.removeRFQFromGroup(clientId, groupId, rfqNumber);
      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to remove RFQ:', err);
      alert(`Failed to remove RFQ: ${err.message}`);
    }
  };

  const handleAcceptRFQ = async (rfqNumber) => {
    try {
      const response = await fetch(getApiUrl(`/api/rfq/${rfqNumber}/accept`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to accept RFQ: ${response.status}`);
      }

      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to accept RFQ:', err);
      alert(`Failed to accept RFQ: ${err.message}`);
    }
  };

  // Helper function to get unit from catalog
  const getUnitForMaterial = (material, subtype) => {
    if (!catalog || !material || !subtype) return 'units';
    
    // Convert kebab-case to Title Case for catalog lookup
    const materialTitle = toTitleCase(material);
    const subtypeTitle = toTitleCase(subtype);
    
    const categoryItems = getCatalogForCategory(catalog, materialTitle);
    const item = categoryItems.find(item => item.name === subtypeTitle);
    return item?.unit || 'units';
  };

  const handleRFQClick = (rfq) => {
    // Convert RFQ to history item format
    const historyItem = {
      id: rfq.id,
      event: `RFQ #${rfq.rfqNumber}`,
      dealer: rfq.clientCompany || 'N/A',
      dealerId: rfq.clientId,
      state: rfq.status || rfq.state || 'N/A',
      quantity: rfq.quantity,
      material: rfq.material,
      subMaterial: rfq.subtype,
      materialCategory: rfq.materialCategory,
      rfqNumber: rfq.rfqNumber,
      rfqId: rfq.id,
      status: rfq.status,
      time: rfq.createdAt,
      notes: rfq.notes || null,
      quote: rfq.quote,
    };
    setSelectedRFQ(historyItem);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedRFQ(null);
  };

  const handleRFQUpdate = (updatedItem) => {
    // Update the RFQ in the groups
    setGroups(prevGroups => {
      return prevGroups.map(group => {
        const rfqIndex = group.rfqs.findIndex(r => r.rfqNumber === updatedItem.rfqNumber);
        if (rfqIndex === -1) return group;

        const updatedRfqs = [...group.rfqs];
        updatedRfqs[rfqIndex] = {
          ...updatedRfqs[rfqIndex],
          status: updatedItem.status,
          state: updatedItem.state,
          quote: updatedItem.quote || updatedRfqs[rfqIndex].quote,
        };

        return {
          ...group,
          rfqs: updatedRfqs
        };
      });
    });
  };

  const handleRejectRFQ = async (rfqNumber) => {
    try {
      const response = await fetch(getApiUrl(`/api/rfq/${rfqNumber}/reject`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to reject RFQ: ${response.status}`);
      }

      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to reject RFQ:', err);
      alert(`Failed to reject RFQ: ${err.message}`);
    }
  };

  const handleAcceptOneRejectOthers = async (acceptRfqNumber, allRfqsInSubcategory) => {
    if (!window.confirm(`Accept RFQ #${acceptRfqNumber} and reject all other RFQs in this subcategory?`)) {
      return;
    }

    try {
      // Accept the selected RFQ
      await handleAcceptRFQ(acceptRfqNumber);

      // Reject all others in the subcategory
      const rejectPromises = allRfqsInSubcategory
        .filter(rfq => rfq.rfqNumber !== acceptRfqNumber)
        .map(rfq => handleRejectRFQ(rfq.rfqNumber));

      await Promise.all(rejectPromises);
      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to accept one and reject others:', err);
      alert(`Failed to complete operation: ${err.message}`);
    }
  };

  const handleRejectAllInGroup = async (group) => {
    const rejectableRfqs = group.rfqs.filter(
      rfq => rfq.status !== 'accepted' && rfq.status !== 'rejected' && rfq.status !== 'awarded'
    );

    if (rejectableRfqs.length === 0) {
      alert('No RFQs available to reject in this group');
      return;
    }

    if (!window.confirm(`Reject all ${rejectableRfqs.length} pending RFQ(s) in group "${group.name}"?`)) {
      return;
    }

    try {
      await Promise.all(
        rejectableRfqs.map(rfq =>
          fetch(getApiUrl(`/api/rfq/${rfq.rfqNumber}/reject`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          })
        )
      );

      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to reject all RFQs:', err);
      alert(`Failed to reject all RFQs: ${err.message}`);
    }
  };

  const handleRejectMaterialRFQs = async (group, material, subcategories) => {
    // Collect all RFQs for this material across all subcategories
    const allMaterialRfqs = Object.values(subcategories).flat();
    const rejectableRfqs = allMaterialRfqs.filter(
      rfq => rfq.status !== 'accepted' && rfq.status !== 'rejected' && rfq.status !== 'awarded'
    );

    if (rejectableRfqs.length === 0) {
      alert(`No RFQs available to reject in ${material}`);
      return;
    }

    if (!window.confirm(`Reject ${rejectableRfqs.length} pending RFQ(s) in ${material}?`)) {
      return;
    }

    try {
      await Promise.all(
        rejectableRfqs.map(rfq =>
          fetch(getApiUrl(`/api/rfq/${rfq.rfqNumber}/reject`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          })
        )
      );

      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to reject material RFQs:', err);
      alert(`Failed to reject RFQs: ${err.message}`);
    }
  };

  const handleRejectSubcategoryRFQs = async (material, subcategory, rfqs) => {
    const rejectableRfqs = rfqs.filter(
      rfq => rfq.status !== 'accepted' && rfq.status !== 'rejected' && rfq.status !== 'awarded'
    );

    if (rejectableRfqs.length === 0) {
      alert(`No RFQs available to reject in ${subcategory}`);
      return;
    }

    if (!window.confirm(`Reject ${rejectableRfqs.length} pending RFQ(s) in ${subcategory}?`)) {
      return;
    }

    try {
      await Promise.all(
        rejectableRfqs.map(rfq =>
          fetch(getApiUrl(`/api/rfq/${rfq.rfqNumber}/reject`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          })
        )
      );

      await loadGroups();
    } catch (err) {
      console.error('[Groups] Failed to reject subcategory RFQs:', err);
      alert(`Failed to reject RFQs: ${err.message}`);
    }
  };

  const groupRFQsByMaterialAndSubcategory = (rfqs) => {
    const grouped = {};
    
    rfqs.forEach(rfq => {
      const material = rfq.material || 'Uncategorized';
      const subcategory = rfq.subtype || 'General';
      
      if (!grouped[material]) {
        grouped[material] = {};
      }
      if (!grouped[material][subcategory]) {
        grouped[material][subcategory] = [];
      }
      grouped[material][subcategory].push(rfq);
    });
    
    // Sort RFQs within each subcategory
    Object.keys(grouped).forEach(material => {
      Object.keys(grouped[material]).forEach(subcategory => {
        grouped[material][subcategory].sort((a, b) => {
          const priceA = a.quote?.price;
          const priceB = b.quote?.price;
          
          // Both have quotes - sort by price ascending
          if (priceA != null && priceB != null) {
            return priceA - priceB;
          }
          // Only A has quote - A comes first
          if (priceA != null && priceB == null) {
            return -1;
          }
          // Only B has quote - B comes first
          if (priceA == null && priceB != null) {
            return 1;
          }
          // Neither has quote - maintain order
          return 0;
        });
      });
    });
    
    return grouped;
  };

  const getStateColor = (status) => {
    const stateColors = {
      "pending": "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200",
      "quoted": "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200",
      "countered": "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200",
      "accepted": "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200",
      "awarded": "bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200",
      "rejected": "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200",
    };
    return stateColors[status?.toLowerCase()] || "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-200";
  };

  if (loading) {
    return (
      <div className="flex-1 p-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <p>Loading groups...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-4 bg-white dark:bg-gray-900">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded p-4 text-red-700 dark:text-red-200">
          <p className="font-semibold">Error loading groups:</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={loadGroups}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="flex-1 p-4 overflow-y-auto bg-white dark:bg-gray-900">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Groups</h2>
          <button
            onClick={() => setShowCreateGroup(!showCreateGroup)}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white font-medium rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Group
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Organize your RFQs into groups. Click on a group name to expand and view RFQs organized by material category.
        </p>
        
        {showCreateGroup && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Create New Group</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !creatingGroup) {
                    handleCreateGroup();
                  }
                }}
                placeholder="Enter group name..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={creatingGroup}
              />
              <button
                onClick={handleCreateGroup}
                disabled={creatingGroup || !newGroupName.trim()}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white font-medium rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {creatingGroup ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => {
                  setShowCreateGroup(false);
                  setNewGroupName("");
                }}
                disabled={creatingGroup}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-100 font-medium rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-lg font-medium mb-2">No groups yet</p>
          <p className="text-gray-500 dark:text-gray-500 text-sm">
            Create a group from the RFQ screen to organize your requests.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(group => {
            const isExpanded = expandedGroups.has(group.id);
            const isDeleting = deletingGroup === group.id;
            const groupedRFQs = groupRFQsByMaterialAndSubcategory(group.rfqs || []);
            const rfqCount = group.rfqs?.length || 0;

            return (
              <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
                {/* Group Header */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="flex items-center gap-3 flex-1 text-left group"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                        {group.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {rfqCount} {rfqCount === 1 ? 'RFQ' : 'RFQs'}
                        {group.createdAt && ` • Created ${formatDate(group.createdAt)}`}
                      </p>
                    </div>
                  </button>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleRejectAllInGroup(group)}
                      className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-sm font-medium"
                    >
                      Reject All
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id, group.name)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Group'}
                    </button>
                  </div>
                </div>

                {/* Group Content - Expanded */}
                {isExpanded && (
                  <div className="p-4">
                    {rfqCount === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-8 text-sm">
                        No RFQs in this group yet. Add RFQs from the RFQ screen.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {Object.entries(groupedRFQs).map(([material, subcategories]) => {
                          const materialKey = `${group.id}-${material}`;
                          const isMaterialExpanded = expandedMaterials.has(materialKey);
                          const materialRFQCount = Object.values(subcategories).reduce((sum, rfqs) => sum + rfqs.length, 0);
                          
                          return (
                            <div key={material} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                              {/* Material Header */}
                              <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-700">
                                <button
                                  onClick={() => toggleMaterial(group.id, material)}
                                  className="flex items-center gap-2 flex-1 hover:bg-gray-150 dark:hover:bg-gray-650 transition-colors rounded px-2 py-1"
                                >
                                  <svg
                                    className={`w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform ${isMaterialExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 flex-1 text-left">
                                    {capitalize(material)}
                                  </h4>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                                    {materialRFQCount} {materialRFQCount === 1 ? 'RFQ' : 'RFQs'}
                                  </span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRejectMaterialRFQs(group, material, subcategories);
                                  }}
                                  className="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 rounded transition-colors"
                                >
                                  Reject All
                                </button>
                              </div>

                              {/* Material Content - Subcategories */}
                              {isMaterialExpanded && (
                                <div className="p-3 space-y-3 bg-white dark:bg-gray-800">
                                  {Object.entries(subcategories).map(([subcategory, rfqs]) => {
                                    const subcategoryKey = `${group.id}-${material}-${subcategory}`;
                                    const isSubcategoryExpanded = expandedSubcategories.has(subcategoryKey);
                                    
                                    return (
                                      <div key={subcategory} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                                        {/* Subcategory Header */}
                                        <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-750">
                                          <button
                                            onClick={() => toggleSubcategory(group.id, material, subcategory)}
                                            className="flex items-center gap-2 flex-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded px-1 py-1"
                                          >
                                            <svg
                                              className={`w-3 h-3 text-gray-500 dark:text-gray-400 transition-transform ${isSubcategoryExpanded ? 'rotate-90' : ''}`}
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1 text-left">
                                              {capitalize(subcategory)}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                              {rfqs.length} {rfqs.length === 1 ? 'item' : 'items'}
                                            </span>
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRejectSubcategoryRFQs(material, subcategory, rfqs);
                                            }}
                                            className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 rounded transition-colors"
                                          >
                                            Reject All
                                          </button>
                                        </div>
                                        
                                        {/* Subcategory Content - RFQ Cards */}
                                        {isSubcategoryExpanded && (
                                          <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 bg-gray-50 dark:bg-gray-800">
                                            {rfqs.map(rfq => (
                                              <div
                                                key={rfq.id}
                                                onClick={() => handleRFQClick(rfq)}
                                                className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-700 hover:shadow-md transition-shadow cursor-pointer"
                                              >
                                                <div className="flex items-start justify-between mb-2">
                                                  <div className="flex-1 min-w-0">
                                                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate" title={rfq.rfqNumber}>
                                                      #{rfq.rfqNumber}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                      {rfq.createdAtFormatted || formatDate(rfq.createdAt)}
                                                    </div>
                                                  </div>
                                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ml-2 ${getStateColor(rfq.status)}`}>
                                                    {rfq.status || 'N/A'}
                                                  </span>
                                                </div>

                                                <div className="space-y-1 text-xs mb-3">
                                                  {rfq.quantity && (
                                                    <div className="text-gray-700 dark:text-gray-300">
                                                      <span className="font-medium">Qty:</span> {rfq.quantity}
                                                    </div>
                                                  )}
                                                  {rfq.location && (
                                                    <div className="text-gray-700 dark:text-gray-300">
                                                      <span className="font-medium">Location:</span> {rfq.location}
                                                    </div>
                                                  )}
                                                  {rfq.clientCompany && (
                                                    <div className="text-gray-700 dark:text-gray-300">
                                                      <span className="font-medium">Client:</span> {rfq.clientCompany}
                                                    </div>
                                                  )}
                                                  {rfq.quote && rfq.quote.price && (
                                                    <div className="text-green-700 dark:text-green-400 font-bold text-lg">
                                                      <span className="text-black dark:text-white font-medium">Quote:</span> ${typeof rfq.quote.price === 'number' ? rfq.quote.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : rfq.quote.price}
                                                      {rfq.quantity && (() => {
                                                        const unit = getUnitForMaterial(rfq.material, rfq.subtype);
                                                        const unitPrice = typeof rfq.quote.price === 'number' && rfq.quantity 
                                                          ? (rfq.quote.price / parseFloat(rfq.quantity)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                          : '0.00';
                                                        return (
                                                          <div className="text-sm text-gray-600 dark:text-gray-400 font-normal mt-0.5">
                                                            ${unitPrice}/{unit}
                                                          </div>
                                                        );
                                                      })()}
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="flex flex-col gap-2">
                                                  <div className="flex gap-2">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAcceptRFQ(rfq.rfqNumber);
                                                      }}
                                                      disabled={rfq.status === 'accepted' || rfq.status === 'rejected' || rfq.status === 'awarded'}
                                                      className="flex-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                      Accept
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRejectRFQ(rfq.rfqNumber);
                                                      }}
                                                      disabled={rfq.status === 'accepted' || rfq.status === 'rejected' || rfq.status === 'awarded'}
                                                      className="flex-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                      Reject
                                                    </button>
                                                  </div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleAcceptOneRejectOthers(rfq.rfqNumber, rfqs);
                                                    }}
                                                    disabled={rfq.status === 'accepted' || rfq.status === 'rejected' || rfq.status === 'awarded'}
                                                    className="w-full px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                  >
                                                    Accept & Reject Others
                                                  </button>
                                                  <div className="flex gap-2">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveRFQ(group.id, rfq.rfqNumber);
                                                      }}
                                                      className="flex-1 px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors text-xs font-medium"
                                                    >
                                                      Remove
                                                    </button>
                                                    {onOpenChat && (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          onOpenChat({
                                                            rfqNumber: rfq.rfqNumber,
                                                            rfqId: rfq.id,
                                                            material: rfq.material,
                                                            subMaterial: rfq.subtype,
                                                            dealer: rfq.clientCompany,
                                                            dealerId: rfq.clientId,
                                                            _openInBottomPanel: true
                                                          });
                                                        }}
                                                        className="flex-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors text-xs font-medium"
                                                      >
                                                        Chat
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    {showDetailsModal && selectedRFQ && (
      <HistoryDetailsModal
        item={selectedRFQ}
        onClose={handleCloseModal}
        onUpdate={handleRFQUpdate}
        clientId={clientId}
        onOpenChat={(rfq) => {
          if (onOpenChat) onOpenChat(rfq);
          handleCloseModal();
        }}
      />
    )}
    </>
  );
}
